/**
 * Auth API · 真后端 + mock 双轨
 *
 * USE_MOCK=true → 走 src/services/mock/users.ts（保留 · v1 砍 API 切回用）
 * USE_MOCK=false → 走 /api/v1/auth/* (apps/api-server/src/api_server/auth/routes.py)
 */
import { ApiError, api, apiConfig, rawFetch } from "./api";
import {
  meToUser,
  type LoginRequestDTO,
  type LoginResponseDTO,
  type MeResponseDTO,
  type MfaSetupResponseDTO,
  type TokenResponseDTO,
  type User,
} from "./types/auth";
import { useAuthStore } from "@/store/authStore";
import { MOCK_USERS } from "./mock/users";

export interface LoginResult {
  user: User;
  tokens: { accessToken: string; refreshToken: string; expiresIn: number };
}

/**
 * MFAFIX-1 · /login 现在有两种结局，调用方（LoginPage）必须分支处理：
 * - success：和原来一样，已写好 store，直接跳转
 * - mfa_enrollment_required：账号必须绑 MFA 但还没绑过——带着 enrollmentToken
 *   跳绑定页，**不能**当成功处理（此时 store 里还没有 accessToken）
 */
export type LoginOutcome =
  | ({ kind: "success" } & LoginResult)
  | { kind: "mfa_enrollment_required"; enrollmentToken: string; expiresIn: number };

export async function login(req: LoginRequestDTO): Promise<LoginOutcome> {
  if (apiConfig.useMock) return mockLogin(req);
  return realLogin(req);
}

/** MFA 绑定页用·带着 enrollmentToken 调 /mfa/setup（工单 Part2）。 */
export async function setupMfaEnrollment(enrollmentToken: string): Promise<MfaSetupResponseDTO> {
  return rawFetch<MfaSetupResponseDTO>("/api/v1/auth/mfa/setup", {
    method: "POST",
    noAuth: true, // 跳过 store 里的（此时还没有的）accessToken 注入
    headers: { Authorization: `Bearer ${enrollmentToken}` },
  });
}

/**
 * MFA 绑定页用·带着 enrollmentToken + 6 位码调 /mfa/verify。成功后后端直接发
 * 正常 access/refresh 令牌对——这里顺手把 store 写好（同 realLogin 收尾一致），
 * 调用方拿到结果后只需要 navigate，不用再单独调一次 login。
 */
export async function verifyMfaEnrollment(
  enrollmentToken: string,
  code: string,
): Promise<LoginResult> {
  const tokens = await rawFetch<TokenResponseDTO>("/api/v1/auth/mfa/verify", {
    method: "POST",
    body: JSON.stringify({ code }),
    noAuth: true,
    headers: { Authorization: `Bearer ${enrollmentToken}` },
  });
  useAuthStore.getState().setTokens({
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    expiresIn: tokens.expires_in,
  });
  const me = await api<MeResponseDTO>("/api/v1/auth/me");
  const user = meToUser(me);
  useAuthStore.getState().setUser(user);
  return {
    user,
    tokens: {
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      expiresIn: tokens.expires_in,
    },
  };
}

/** 自助修改密码（2026-07-18 补缺·POST /api/v1/auth/change-password） */
export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  await api<{ ok: boolean }>("/api/v1/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  });
}

/**
 * 忘记密码（PWRESET-1 · POST /api/v1/auth/forgot-password）
 * 后端无论邮箱存不存在都返回同一句话（防枚举）——前端只展示 message，不做"邮箱不存在"分支。
 */
export async function forgotPassword(email: string): Promise<{ message: string }> {
  return rawFetch<{ ok: boolean; message: string }>("/api/v1/auth/forgot-password", {
    method: "POST",
    noAuth: true,
    body: JSON.stringify({ email }),
  });
}

/** 凭邮件令牌设新密码（PWRESET-1 · POST /api/v1/auth/reset-password）。400=链接无效/过期 · 422=强度不足 */
export async function resetPasswordByToken(token: string, newPassword: string): Promise<void> {
  await rawFetch<{ ok: boolean }>("/api/v1/auth/reset-password", {
    method: "POST",
    noAuth: true,
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}

export async function logout(): Promise<void> {
  if (apiConfig.useMock) {
    useAuthStore.getState().logout();
    return;
  }
  try {
    await api<void>("/api/v1/auth/logout", { method: "POST" });
  } catch {
    // 即使后端 logout 失败也清本地
  } finally {
    useAuthStore.getState().logout();
  }
}

export async function fetchMe(): Promise<User> {
  if (apiConfig.useMock) {
    const u = useAuthStore.getState().user;
    if (!u) throw new ApiError(401, "not logged in (mock)");
    return u;
  }
  const me = await api<MeResponseDTO>("/api/v1/auth/me");
  return meToUser(me);
}

// ── 真 API ────────────────────────────────────────────────────────────────

async function realLogin(req: LoginRequestDTO): Promise<LoginOutcome> {
  const res = await rawFetch<LoginResponseDTO>("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify(req),
    noAuth: true,
    noRefresh: true,
  });

  if (res.mfa_enrollment_required) {
    if (!res.enrollment_token) throw new ApiError(500, "后端未返回 enrollment_token");
    return {
      kind: "mfa_enrollment_required",
      enrollmentToken: res.enrollment_token,
      expiresIn: res.expires_in,
    };
  }
  if (!res.access_token || !res.refresh_token) {
    throw new ApiError(500, "登录响应缺少令牌");
  }

  // 临时写 access token 进 store · /me 才能带 Bearer
  useAuthStore.getState().setTokens({
    accessToken: res.access_token,
    refreshToken: res.refresh_token,
    expiresIn: res.expires_in,
  });

  const me = await api<MeResponseDTO>("/api/v1/auth/me");
  const user = meToUser(me);
  useAuthStore.getState().setUser(user);

  return {
    kind: "success",
    user,
    tokens: {
      accessToken: res.access_token,
      refreshToken: res.refresh_token,
      expiresIn: res.expires_in,
    },
  };
}

// ── Mock ──────────────────────────────────────────────────────────────────

async function mockLogin(req: LoginRequestDTO): Promise<LoginOutcome> {
  const user = MOCK_USERS.find((u) => u.email === req.email) ?? MOCK_USERS[0];
  if (!user) throw new ApiError(401, "mock 用户列表为空");
  const tokens = {
    accessToken: `mock-access-${user.id}`,
    refreshToken: `mock-refresh-${user.id}`,
    expiresIn: 60 * 60,
  };
  // mock 用户不会撞上 MFA 绑定死锁场景（mock 模式本来就跳过真后端）——恒 success。
  return { kind: "success", user, tokens };
}
